/**
 * Uses the relevant AoF or Status Tracking review to gate order completion.
 * An unresolved legacy AoF review keeps its original message, even if fax loading fails.
 *
 * @module useAuthLetterReviewMarkCompleteBlocker
 */
import { useMemo } from "react";
import { MarkCompleteValidationMessages } from "../../../constants/markCompleteValidationMessages";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { useOrderFaxLettersContext } from "../../../context/orderFaxLettersContext";
import {
  hasLegacyAuthLetterEvidence,
  shouldDisableMarkCompleteForAuthLetterReviewStatus,
} from "../utils/authLetterReviewMarkCompleteGuard";
import { getAllJCodesFromAuthOnFile } from "../utils/existingDocsToLs";

/**
 * Recomputes whenever saved auth data or the shared, scoped fax review result changes.
 *
 * @returns Whether review blocks completion and the reason to display
 */
export const useAuthLetterReviewMarkCompleteBlocker = () => {
  const { authOnFileDetails } = useDataAccumulatorForSingleOrder();
  const { byJCodeId, status } = useOrderFaxLettersContext();
  const blocked = useMemo(
    () =>
      shouldDisableMarkCompleteForAuthLetterReviewStatus(
        authOnFileDetails?.auth_on_file,
        status === "ready" ? byJCodeId : undefined,
      ),
    [authOnFileDetails?.auth_on_file, byJCodeId, status],
  );
  const legacyReviewBlocked =
    shouldDisableMarkCompleteForAuthLetterReviewStatus({
      auth_entries: [
        {
          j_codes: getAllJCodesFromAuthOnFile(
            authOnFileDetails?.auth_on_file,
          )?.filter(hasLegacyAuthLetterEvidence),
        },
      ],
    });
  return {
    blocked,
    message: !blocked
      ? undefined
      : !legacyReviewBlocked && status === "loading"
        ? MarkCompleteValidationMessages.AUTH_LETTER_REVIEW_CHECKING
        : !legacyReviewBlocked && status === "error"
          ? MarkCompleteValidationMessages.AUTH_LETTER_REVIEW_UNAVAILABLE
          : MarkCompleteValidationMessages.AUTH_LETTER_REVIEW_PENDING,
  };
};
